import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Search, UserRound, Eye, KeyRound, Download } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  executive: "Executive Management",
  ops_mgmt: "Operations Management",
  bu_manager: "Business Unit Manager",
  bu_support: "Business Unit Support",
  admin: "General Administration",
  finance_admin: "Financial Administration",
  field_worker: "Field Worker",
  client: "Client",
  sustainability_advisor: "Sustainability Advisor",
  hseq_auditor: "HSEQ Auditor",
};
const ROLE_TONE = {
  executive: "bg-gbe text-white",
  ops_mgmt: "bg-blue-100 text-blue-800 border-blue-200",
  bu_manager: "bg-emerald-100 text-emerald-800 border-emerald-200",
  bu_support: "bg-teal-100 text-teal-800 border-teal-200",
  admin: "bg-violet-100 text-violet-800 border-violet-200",
  finance_admin: "bg-indigo-100 text-indigo-800 border-indigo-200",
  field_worker: "bg-amber-100 text-amber-800 border-amber-200",
  client: "bg-rose-100 text-rose-800 border-rose-200",
  hseq_auditor: "bg-cyan-100 text-cyan-800 border-cyan-200",
};

const EMPTY = { email: "", name: "", role: "field_worker", business_unit_id: null, client_name: null };

export function UsersTab() {
  const { can } = usePermissions();
  const { user: me, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const meRole = (me?.role || "").toLowerCase();
  const canImpersonate = meRole === "executive";
  // Non-executive user-admins (Operations Mgmt) can assign any role except Executive
  const roleOptions = Object.entries(ROLE_LABEL).filter(
    ([code]) => meRole === "executive" || code !== "executive");
  const [users, setUsers] = useState([]);
  const [bus, setBus] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [resetting2fa, setResetting2fa] = useState(null);
  // Invitation modal state (shown after "Send invitation" succeeds)
  const [inviteResult, setInviteResult] = useState(null);
  const [invites, setInvites] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b, iv] = await Promise.all([
        api.get("/org/users"), api.get("/org/business-units"),
        api.get("/org/invites?status=pending").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data); setBus(b.data); setInvites(iv.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Client list is only needed for the "Client" role dropdown — lazy-load it
  // (minimal payload) the first time a client dialog actually needs it so the
  // 1,200+ client records never weigh down the initial Users page load.
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const ensureClients = async () => {
    if (clientsLoaded) return;
    try {
      const { data } = await api.get("/master/clients?minimal=true");
      setClients(data);
    } finally { setClientsLoaded(true); }
  };

  const buMap = useMemo(() => Object.fromEntries(bus.map((b) => [b.id, b])), [bus]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    // Merge active users + pending invitations into a single list.
    // Pending invitations render with is_pending=true and a "Invite sent" badge.
    const userEmails = new Set(users.map((u) => (u.email || "").toLowerCase()));
    const inviteRows = (invites || [])
      .filter((inv) => !userEmails.has((inv.email || "").toLowerCase()))
      .map((inv) => ({
        id: `invite-${inv.id}`,
        invite_id: inv.id,
        is_pending: true,
        email: inv.email,
        name: inv.name,
        role: inv.role,
        business_unit_id: inv.business_unit_id || null,
        client_name: inv.client_name || null,
        totp_enabled: false,
        expires_at: inv.expires_at,
      }));
    return [...inviteRows, ...users].filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (ql && !`${u.email} ${u.name}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [users, invites, q, roleFilter]);
  const { sorted: sortedUsers, sort: userSort, setSort: setUserSort } = useSortable(filtered);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (u) => { if (u.role === "client") ensureClients(); setEditing(u); setForm({ email: u.email, name: u.name || "", role: u.role || "field_worker", business_unit_id: u.business_unit_id || null, client_name: u.client_name || null, active: u.active !== false }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        // Send an invitation instead of creating a user directly
        const { data } = await api.post("/org/invites", {
          email: form.email, name: form.name, role: form.role,
          business_unit_id: form.business_unit_id, client_name: form.client_name,
        });
        setInviteResult({
          email: form.email,
          name: form.name,
          role: form.role,
          invite_url: data.invite_url,
          email_sent: data.email_sent,
          email_error: data.email_error,
          expires_at: data.expires_at,
        });
        close();
      } else {
        const payload = { name: form.name, role: form.role, business_unit_id: form.business_unit_id, client_name: form.client_name, active: form.active };
        await api.put(`/org/users/${editing.id}`, payload);
        toast.success("Saved");
        close();
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invitation for ${inv.email}?`)) return;
    try {
      await api.post(`/org/invites/${inv.id}/revoke`);
      toast.success("Invitation revoked");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  const exportPendingInvites = () => {
    const activeEmails = new Set(users.map((u) => (u.email || "").toLowerCase()));
    const pend = (invites || [])
      .filter((inv) => !activeEmails.has((inv.email || "").toLowerCase()))
      .slice()
      .sort((a, b) => (a.invited_at || "").localeCompare(b.invited_at || ""));
    if (!pend.length) { toast.info("No pending invites to export"); return; }
    const now = new Date();
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Name", "Email", "Role", "Invited", "Expires", "Status", "Invited By"];
    const lines = pend.map((inv) => {
      const expired = inv.expires_at && new Date(inv.expires_at) < now;
      return [
        inv.name || "",
        inv.email || "",
        ROLE_LABEL[inv.role] || inv.role || "",
        (inv.invited_at || "").slice(0, 10),
        (inv.expires_at || "").slice(0, 10),
        expired ? "Expired" : "Awaiting acceptance",
        inv.invited_by || "",
      ].map(esc).join(",");
    });
    const csv = [header.join(","), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pending-invites-${now.toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${pend.length} pending invite${pend.length === 1 ? "" : "s"}`);
  };

  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/org/invites/${inv.id}/resend`);
      setInviteResult({
        email: inv.email, name: inv.name, role: inv.role,
        invite_url: data.invite_url, email_sent: data.email_sent, email_error: data.email_error,
      });
      if (data.email_sent) toast.success(`Invite re-sent to ${inv.email} — expiry extended 72h`);
      else toast.error(`Email failed (${data.email_error || "unknown"}) — copy the invite link instead`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/org/users/${u.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(
      `Reset the authenticator (2FA) for ${u.name || u.email}?\n\n` +
      `Their current authenticator will stop working and they'll be prompted to set up a new one at their next sign-in. Use this when a user loses their phone.`
    )) return;
    setResetting2fa(u.id);
    try {
      await api.post(`/org/users/${u.id}/reset-2fa`);
      toast.success(`2FA reset for ${u.email} — they'll re-enrol at next login`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setResetting2fa(null); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(
      `You are about to view the app as ${u.name || u.email} (${ROLE_LABEL[u.role] || u.role}).\n\n` +
      `This session will be READ-ONLY and expire in 30 minutes. All actions are audited.\n\nContinue?`
    )) return;
    setImpersonatingId(u.id);
    try {
      await startImpersonation(u.id);
      // Full reload so every module refetches with the target user's scope
      // (mirrors the exit flow in ImpersonationBanner).
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not start impersonation");
      setImpersonatingId(null);
    }
  };

  const needsBU = ["bu_manager", "bu_support", "field_worker"].includes(form.role);
  const needsClient = form.role === "client";

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
        <div className="mr-auto">
          <h3 className="font-display text-lg font-semibold text-slate-900">Users</h3>
          <p className="text-xs text-slate-500">{filtered.length} of {users.length + invites.length} shown</p>
        </div>
        <div className="relative w-[260px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="pl-9 h-9 bg-slate-50 border-slate-200" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="users-role-filter" className="w-[220px] h-9"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(ROLE_LABEL).map(([code, label]) => (<SelectItem key={code} value={code}>{label}</SelectItem>))}
          </SelectContent>
        </Select>
        {can("org_mgmt", "view") && invites.length > 0 && (
          <Button data-testid="users-export-pending-btn" variant="outline" onClick={exportPendingInvites} className="h-9">
            <Download className="w-4 h-4 mr-1.5" /> Export pending invites
          </Button>
        )}
        {can("org_mgmt", "create") && (
          <Button data-testid="user-new-btn" onClick={openNew} className="bg-gbe hover:bg-gbe-hover text-white h-9">
            <Plus className="w-4 h-4 mr-1.5" /> Invite user
          </Button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <SortableTh label="User" k="name" sort={userSort} setSort={setUserSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Role" k="role" sort={userSort} setSort={setUserSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Business Unit" k="business_unit_id" sort={userSort} setSort={setUserSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Client / Scope" k="client_name" sort={userSort} setSort={setUserSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="2FA" k="totp_enabled" sort={userSort} setSort={setUserSort} className="text-right font-medium px-5 py-2.5" />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6} className="p-4"><div className="h-6 rounded shimmer" /></td></tr>
          ) : sortedUsers.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-slate-100 hover:bg-slate-50/60 ${u.is_pending ? "bg-amber-50/40" : ""}`}>
              <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${u.is_pending ? "bg-amber-100 border-amber-200" : "bg-slate-100 border-slate-200"}`}>
                    <UserRound className={`w-4 h-4 ${u.is_pending ? "text-amber-700" : "text-slate-500"}`} />
                  </div>
                  <div>
                    <div className="font-medium text-slate-800 flex items-center gap-2">
                      {u.name}
                      {u.is_pending && (
                        <span data-testid={`pending-badge-${u.email}`} className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                          Invite sent
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {u.email}
                      {u.is_pending && u.expires_at && <span className="ml-2 text-amber-700">· expires {u.expires_at.slice(0, 10)}</span>}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-5 py-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_TONE[u.role] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>{ROLE_LABEL[u.role] || u.role}</span>
              </td>
              <td className="px-5 py-3 text-slate-700">{buMap[u.business_unit_id]?.name || "—"}</td>
              <td className="px-5 py-3 text-slate-600 truncate max-w-[220px]">{u.client_name || "—"}</td>
              <td className="px-5 py-3 text-right">
                {u.is_pending ? (
                  <span className="text-[10px] text-slate-400">—</span>
                ) : (
                  <span
                    title={u.totp_enabled ? "Authenticator app enrolled" : "2FA is mandatory org-wide — this user will be forced to set up an authenticator at their next sign-in and cannot use the app until they do."}
                    className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${u.totp_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                    data-testid={`user-2fa-${u.email}`}
                  >{u.totp_enabled ? "Enabled" : "Forced at next login"}</span>
                )}
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {u.is_pending ? (
                  <>
                    <Button data-testid={`invite-resend-${u.email}`} onClick={() => resendInvite({ id: u.invite_id, email: u.email, name: u.name, role: u.role })} variant="ghost" size="sm" className="h-8 text-xs">Resend</Button>
                    <Button data-testid={`invite-revoke-${u.email}`} onClick={() => revokeInvite({ id: u.invite_id, email: u.email })} variant="ghost" size="sm" className="h-8 text-xs text-rose-600">Revoke</Button>
                  </>
                ) : (
                  <>
                    {canImpersonate && (me?.email || "").toLowerCase() !== (u.email || "").toLowerCase() && (
                      <Button
                        data-testid={`user-impersonate-${u.email}`}
                        onClick={() => impersonate(u)}
                        disabled={impersonatingId === u.id}
                        variant="ghost"
                        size="sm"
                        className="h-8 text-amber-700 hover:bg-amber-50"
                        title="View app as this user (read-only, 30 min)"
                      >
                        {impersonatingId === u.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                    {can("org_mgmt", "edit") && (
                      <Button data-testid={`user-edit-${u.email}`} onClick={() => openEdit(u)} variant="ghost" size="sm" className="h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                    )}
                    {(me?.role || "").toLowerCase() === "executive" && (me?.email || "").toLowerCase() !== (u.email || "").toLowerCase() && (
                      <Button
                        data-testid={`user-reset2fa-${u.email}`}
                        onClick={() => reset2fa(u)}
                        disabled={resetting2fa === u.id}
                        variant="ghost"
                        size="sm"
                        className="h-8 text-slate-600 hover:bg-slate-100"
                        title="Reset this user's authenticator (2FA) — they re-enrol at next login"
                      >
                        {resetting2fa === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                    {can("org_mgmt", "delete") && (
                      <Button onClick={() => del(u)} variant="ghost" size="sm" className="h-8 text-rose-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Invitation result modal — shows email delivery status + copy-link fallback */}
      <Dialog open={!!inviteResult} onOpenChange={(o) => { if (!o) { setInviteResult(null); load(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Invitation {inviteResult?.email_sent ? "sent" : "created"}</DialogTitle>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                {inviteResult.email_sent
                  ? <>An invitation email was sent to <span className="font-medium">{inviteResult.email}</span>.</>
                  : <>Could not send email automatically (<span className="text-rose-600">{inviteResult.email_error || "no email provider configured"}</span>). Copy the link below and send it to {inviteResult.name}.</>}
              </p>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">One-time invite link</div>
              <div className="flex items-stretch gap-2">
                <Input readOnly value={inviteResult.invite_url} data-testid="invite-url-input" className="text-xs font-mono bg-slate-50" />
                <Button data-testid="invite-copy-btn"
                        onClick={() => { navigator.clipboard.writeText(inviteResult.invite_url); toast.success("Link copied"); }}
                        variant="outline">Copy</Button>
              </div>
              <p className="text-xs text-slate-500">Link expires {inviteResult.expires_at?.slice(0,10) || "in 72 hours"}. Anyone with this URL can activate the account.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setInviteResult(null); load(); }} className="bg-gbe hover:bg-gbe-hover text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing === "new" ? "Invite user" : `Edit ${editing?.email}`}</DialogTitle></DialogHeader>
          {/* Autofill trap — hidden decoy inputs consume any browser autocomplete request so the
              real fields below stay untouched (Chrome/Edge otherwise offer a "suggested password"
              popup on any dialog with email + name fields). */}
          <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, height: 0, width: 0, overflow: "hidden" }}>
            <input type="text" name="fake-user" tabIndex={-1} autoComplete="username" />
            <input type="password" name="fake-pass" tabIndex={-1} autoComplete="new-password" />
          </div>
          <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="text"
                  name={`email_${Math.random().toString(36).slice(2, 8)}`}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={editing !== "new"}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Full name</Label>
                <Input
                  type="text"
                  name={`name_${Math.random().toString(36).slice(2, 8)}`}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => { if (v === "client") ensureClients(); setForm({ ...form, role: v, business_unit_id: null, client_name: null }); }}>
                  <SelectTrigger className="mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(([code, label]) => (<SelectItem key={code} value={code}>{label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {editing !== "new" && (
                <div>
                  <Label>Password reset</Label>
                  <p className="text-xs text-slate-500 mt-1">To reset a password, revoke the user and re-invite them.</p>
                </div>
              )}
            </div>
            {needsBU && (
              <div>
                <Label>Business Unit</Label>
                <Select value={form.business_unit_id || ""} onValueChange={(v) => setForm({ ...form, business_unit_id: v || null })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select BU" /></SelectTrigger>
                  <SelectContent>
                    {bus.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {needsClient && (
              <div>
                <Label>Assigned client</Label>
                <div className="mt-1">
                  <SearchableSelect
                    value={form.client_name || ""}
                    onChange={(v) => setForm({ ...form, client_name: v || null })}
                    options={clients.map((c) => ({ value: c.name, label: c.name }))}
                    placeholder="Search & select client…"
                    width="w-full"
                    testid="user-form-client-select"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">This user will only see this client's data across the portal, asset intelligence and reports.</p>
              </div>
            )}
          </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="user-form-save" onClick={save} disabled={saving} className="bg-gbe hover:bg-gbe-hover text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing === "new" ? "Send invitation" : "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
